import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import type { Theme } from '@mui/material';
import { Box, IconButton, SvgIcon, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Menu01Icon from '@untitled-ui/icons-react/build/esm/Menu01';
import type { NavColor } from 'src/types/settings';
import type { Section } from '../config';
import { MobileNav } from '../mobile-nav';
import { SideNav, SIDE_NAV_WIDTH } from './side-nav';
import { TopNav, TOP_NAV_HEIGHT } from './top-nav';
import { SideNavV2, SIDE_NAV_V2_WIDTH } from './side-nav-v2';
import { TopNavV2 } from './top-nav-v2';
import { useMobileNav } from './use-mobile-nav';
import { useVersion } from 'src/hooks/use-version';

interface VerticalLayoutProps {
  children?: ReactNode;
  navColor?: NavColor;
  sections?: Section[];
}

export const VerticalLayout: FC<VerticalLayoutProps> = (props) => {
  const { children, sections, navColor } = props;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const mobileNav = useMobileNav();
  const { version } = useVersion();
  const isV2 = version === '2';

  return (
    <>
      {/* Background layer */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'hidden',
          bgcolor: isV2 ? (isDarkMode ? '#0a0a0c' : '#f1f5f9') : (isDarkMode ? '#0a0a0b' : '#f8fafc'),
        }}
      >
        {/* Ambient lighting - v1 only (v2 is clean/flat per reference) */}
        {!isV2 && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '-5%',
                width: { xs: 400, md: 600 },
                height: { xs: 400, md: 600 },
                borderRadius: '50%',
                background: isDarkMode
                  ? 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
                filter: 'blur(80px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '15%',
                left: '-5%',
                width: { xs: 350, md: 500 },
                height: { xs: 350, md: 500 },
                borderRadius: '50%',
                background: isDarkMode
                  ? 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, transparent 70%)',
                filter: 'blur(70px)',
              }}
            />
          </>
        )}
      </Box>

      {isV2 ? (
        <>
          {lgUp && <SideNavV2 />}
          {!lgUp && (
            <SideNavV2
              open={mobileNav.open}
              onClose={mobileNav.handleClose}
            />
          )}
        </>
      ) : (
        <>
          <TopNav
            onMobileNavOpen={mobileNav.handleOpen}
            sideNavWidth={SIDE_NAV_WIDTH}
          />
          {lgUp && (
            <SideNav
              color={navColor}
              sections={sections}
            />
          )}
          {!lgUp && (
            <MobileNav
              color={navColor}
              onClose={mobileNav.handleClose}
              open={mobileNav.open}
              sections={sections}
            />
          )}
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          flex: '1 1 auto',
          maxWidth: '100%',
          minHeight: '100vh',
          bgcolor: 'transparent',
          pl: isV2 ? { lg: `${SIDE_NAV_V2_WIDTH}px` } : { lg: `${SIDE_NAV_WIDTH}px` },
          pt: 0,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            width: '100%',
            ...(isV2 ? {
              mt: { lg: 0.5 },
              mr: { lg: 0.5 },
              mb: { lg: 0.5 },
              ml: { lg: 0.25 },
              borderRadius: { lg: '12px' },
              bgcolor: isDarkMode ? '#161618' : '#F2F8FC',
              border: { lg: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' },
              overflow: 'hidden',
              // Tighten page content spacing — less gap between content and edges
              '& > main': {
                pt: '12px !important',
                pb: '16px !important',
                px: '8px !important',
              },
            } : {
              borderRadius: '7px',
              overflow: 'hidden',
            }),
          }}
        >
          {isV2 && <TopNavV2 onMobileNavOpen={mobileNav.handleOpen} />}
          {children}
        </Box>
      </Box>
    </>
  );
};

VerticalLayout.propTypes = {
  children: PropTypes.node,
  navColor: PropTypes.oneOf<NavColor>(['blend-in', 'discreet', 'evident']),
  sections: PropTypes.array
};
